import React, {Component, Fragment} from 'react';
import {connect} from "react-redux";
import H from "history";
import Table, {TableColumn} from '../../Table';
import ApiService from '../../ApiService';
import DispatchProps from '../../../utils/DispatchProps';
import LoadingCard from '../../LoadingCard';
import AnalyticsDateControls from '../../AnalyticsDateControls';
import {IRootState} from '../../../store';
import {TableRowProps} from '../../TableRow';
import {cellForColumn} from './SubmissionAssignmentAnalyticsUtils';
import AcademicYearPicker from '../../AcademicYearPicker';
import SchoolMenu from '../../SchoolMenu';
import SimpleDoughnut from "../../assignment-analytics/SimpleDoughnut";
import {AnalyticsDoughnutPalette} from "../../assignment-analytics/AnalyticsDoughnutUtils";
import SchoolPageTitle from '../../SchoolPageTitle';
import SubjectSubmissionResponse from "../../../models/analytics/SubjectSubmissionResponse";
import MultiDoughnut from "../../assignment-analytics/MulitDoughnut";
import AnalyticsControlBar from "../../assignment-analytics/AnalyticsControlBar";
import {ClassAssignmentSummary} from "../../../models/analytics/AssignmentSubmissionSummaries";

interface SubjectSubmissionAnalyticsPageProps {
    tenantId: string;
    schoolCode: string
    history: H.History;
    apiService: ApiService;
    startDate?: string,
    endDate?: string,
    academicYear: number,
    subjectCode: string;
}

interface SubjectSubmissionAnalyticsPageState {
    result?: SubjectSubmissionResponse;
}

class SubjectSubmissionAnalyticsPage extends Component<SubjectSubmissionAnalyticsPageProps & DispatchProps, SubjectSubmissionAnalyticsPageState> {
    static displayName = SubjectSubmissionAnalyticsPage.name;

    private static readonly columns: TableColumn[] = [
        { title: "Class Name", propertyPath: "classDisplayName", enableSort: true, flex: 4 },
        { title: "Assignments Set", propertyPath: "assignmentsTotal", enableSort: true, flex: 2 },
        { title: "Not Completed", propertyPath: "submissionsNotCompletedPercentage", extraDetail: "Not submitted within 2 weeks of due date", enableSort: true, flex: 2 },
        { title: "Late", propertyPath: "submissionsSubmittedAfterDueDatePercentage", extraDetail: "Submitted within 2 weeks after the due date", enableSort: true, flex: 2 },
        { title: "On Time", propertyPath: "submissionsSubmittedBeforeDueDatePercentage", extraDetail: "Submitted before the due date", enableSort: true, flex: 2 },
    ];

    constructor(props: SubjectSubmissionAnalyticsPageProps & DispatchProps) {
        super(props);
        this.state = {
            result: undefined
        }
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear && this.props.subjectCode) {
            await this.getResult();
        }
    }

    async componentDidUpdate(prevProps: Readonly<SubjectSubmissionAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear || this.props.subjectCode !== prevProps.subjectCode) {
            await this.getResult();
        }
    }

    private async getResult(): Promise<void> {
        this.setState({
            result: undefined
        });

        const result = await this.props.apiService.getSubjectSubmissionAnalytics(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!,this.props.subjectCode);

        this.setState({
            result
        });
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private readonly rowProps = (item: ClassAssignmentSummary): TableRowProps => {
        return {
            enableRowLink: true,
            to: `/manage/${this.props.tenantId}/${this.props.schoolCode}/class-assignment-analytics/${item.classId}?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`
        };
    }

    private buildCharts = (): JSX.Element | undefined => {
        return (
            <div className={"card flex-row flex--justify-content-space-around p-05rem"} style={{marginTop: "0.25rem"}}>
                <div className={"number-stat"}>
                    <div className="number-stat__number number-stat__number--small">
                        {this.state.result?.summary.classesTotal}
                    </div>
                    <div className={"number-stat__description"}>Classes</div>
                </div>

                <SimpleDoughnut
                    title={"Classes that have set assignments"}
                    aValue={this.state.result!.summary.classesWithAssignmentsPercentage} aLabel={"Have set assignments"} aColour={AnalyticsDoughnutPalette.green}
                    bValue={100 - this.state.result!.summary.classesWithAssignmentsPercentage} bLabel={"Have not set any assignments"} bColour={AnalyticsDoughnutPalette.grey}/>
                
                <MultiDoughnut title={"Submissions"} data={
                    [
                        {value: this.state.result!.summary.submissionsSubmittedAfterDueDatePercentage,label: "On Time",colour: AnalyticsDoughnutPalette.green},
                        {value: this.state.result!.summary.submissionsSubmittedAfterDueDatePercentage,label: "Late",colour: AnalyticsDoughnutPalette.amber},
                        {value: this.state.result!.summary.submissionsNotCompletedPercentage,label: "Not Completed",colour: AnalyticsDoughnutPalette.red}
                    ]
                }/>
            </div>
        );
    }

    private buildResults(): JSX.Element {
        if (this.state.result === undefined)
        {
            return <LoadingCard />;
        }
        else
        {
            return (
                <Fragment>

                    {this.buildCharts()}

                    <Table
                        title={`${this.state.result!.summary.subjectName} Classes`}
                        items={this.state.result.items}
                        columns={SubjectSubmissionAnalyticsPage.columns}
                        cellForColumn={cellForColumn}
                        rowPropsForItem={this.rowProps}
                        defaultSortColumnPath={''}
                        enableSort={true}>
                    </Table>
                </Fragment>
            );
        }
    }

    render() {
        
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title={`Assignment Analytics - ${this.state.result?.summary.subjectName}`} reverse={true} />
                <SchoolMenu prefix="Assignment Analytics" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/assignment-analytics-overview?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`} suffix={this.state.result?.summary?.subjectName} />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <AnalyticsControlBar tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} current={"subject"} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} onAcademicYearChanged={this.onAcademicYearChanged} history={this.props.history}/>
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): SubjectSubmissionAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            subjectCode: ownProps.match.params.subjectCode,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear
        };
    }
)(SubjectSubmissionAnalyticsPage)
